import {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import DataTable from "components/table/DataTable";

import {
    getAppraisals,
    deleteAppraisal,
    submitAppraisal,
    approveAppraisal,
    rejectAppraisal,
} from "services/api/appraisalApi";

import {
    showSuccess,
    showError,
} from "utils/toast";

import AppraisalModal from "./AppraisalModal";

import {
    appraisalColumns,
} from "./columns";


export default function Appraisals() {

    const navigate =
        useNavigate();


    // =========================================================
    // STATE
    // =========================================================

    const [
        appraisals,
        setAppraisals,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        page,
        setPage,
    ] = useState(1);


    const [
        pagination,
        setPagination,
    ] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
    });


    const [
        search,
        setSearch,
    ] = useState("");


    const [
        status,
        setStatus,
    ] = useState("");


    const [
        openModal,
        setOpenModal,
    ] = useState(false);


    const [
        selectedItem,
        setSelectedItem,
    ] = useState(null);


    // =========================================================
    // CURRENT USER
    // =========================================================
    //
    // Don't force permissions here.
    // Backend remains the source of truth.
    //
    // This object can be replaced with your auth context
    // later if your application already exposes currentUser.
    // =========================================================

    const currentUser =
        JSON.parse(
            localStorage.getItem(
                "user"
            ) || "null"
        );


    // =========================================================
    // LOAD DATA
    // =========================================================

    const loadData =
        async () => {

            try {

                setLoading(
                    true
                );


                const response =
                    await getAppraisals(
                        page,
                        10,
                        {
                            status:
                                status ||
                                undefined,
                        }
                    );


                console.log(
                    "APPRAISALS API RESPONSE:",
                    response
                );


                const records =
                    response?.data?.appraisals ||
                    [];


                const paginationData =
                    response?.data?.pagination ||
                    {
                        total: 0,
                        page,
                        limit: 10,
                        totalPages: 0,
                    };


                setPagination(
                    paginationData
                );


                /*
                |--------------------------------------------------------------------------
                | LOCAL SEARCH
                |--------------------------------------------------------------------------
                */

                let filteredRecords =
                    Array.isArray(
                        records
                    )
                        ? records
                        : [];


                const searchValue =
                    search
                        .trim()
                        .toLowerCase();


                if (
                    searchValue
                ) {

                    filteredRecords =
                        filteredRecords.filter(
                            (item) => {

                                const schoolName =
                                    item
                                        ?.school
                                        ?.name
                                        ?.toLowerCase() ||
                                    "";


                                const employeeName =
                                    item
                                        ?.employee
                                        ?.name
                                        ?.toLowerCase() ||
                                    "";


                                const competitor =
                                    item
                                        ?.competitor
                                        ?.toLowerCase() ||
                                    "";


                                const decisionMaker =
                                    item
                                        ?.decisionMaker
                                        ?.toLowerCase() ||
                                    "";


                                return (
                                    schoolName.includes(
                                        searchValue
                                    ) ||

                                    employeeName.includes(
                                        searchValue
                                    ) ||

                                    competitor.includes(
                                        searchValue
                                    ) ||

                                    decisionMaker.includes(
                                        searchValue
                                    )
                                );

                            }
                        );

                }


                setAppraisals(
                    filteredRecords
                );

            } catch (
                error
            ) {

                console.error(
                    "Appraisals load error:",
                    error
                );


                setAppraisals(
                    []
                );


                showError(
                    error
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to load appraisals."
                );

            } finally {

                setLoading(
                    false
                );

            }

        };


    // =========================================================
    // EFFECT
    // =========================================================

    useEffect(
        () => {

            loadData();

        },
        [
            page,
            status,
            search,
        ]
    );


    // =========================================================
    // ADD
    // =========================================================

    const handleAdd =
        () => {

            setSelectedItem(
                null
            );

            setOpenModal(
                true
            );

        };


    // =========================================================
    // EDIT
    // =========================================================

    const handleEdit =
        (item) => {

            setSelectedItem(
                item
            );

            setOpenModal(
                true
            );

        };


    // =========================================================
    // VIEW
    // =========================================================

    const handleView =
        (item) => {

            navigate(
                `/admin/appraisals/${item.id}`
            );

        };


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit =
        async (
            item
        ) => {

            const id =
                item?.id;


            if (!id) {
                return;
            }


            if (
                !window.confirm(
                    "Submit this appraisal?"
                )
            ) {
                return;
            }


            try {

                await submitAppraisal(
                    id
                );


                showSuccess(
                    "Appraisal submitted successfully."
                );


                await loadData();

            } catch (
                error
            ) {

                console.error(
                    "Submit appraisal error:",
                    error
                );


                showError(
                    error
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to submit appraisal."
                );

            }

        };


    // =========================================================
    // APPROVE
    // =========================================================

    const handleApprove =
        async (
            item
        ) => {

            const id =
                item?.id;


            if (!id) {
                return;
            }


            if (
                !window.confirm(
                    "Approve this appraisal?"
                )
            ) {
                return;
            }


            try {

                await approveAppraisal(
                    id,
                    {}
                );


                showSuccess(
                    "Appraisal approved successfully."
                );


                await loadData();

            } catch (
                error
            ) {

                console.error(
                    "Approve appraisal error:",
                    error
                );


                showError(
                    error
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to approve appraisal."
                );

            }

        };


    // =========================================================
    // REJECT
    // =========================================================

    const handleReject =
        async (
            item
        ) => {

            const id =
                item?.id;


            if (!id) {
                return;
            }


            const reason =
                window.prompt(
                    "Enter rejection reason:"
                );


            if (
                !reason ||
                !reason.trim()
            ) {
                return;
            }


            try {

                await rejectAppraisal(
                    id,
                    {
                        reason:
                            reason.trim(),
                    }
                );


                showSuccess(
                    "Appraisal rejected successfully."
                );


                await loadData();

            } catch (
                error
            ) {

                console.error(
                    "Reject appraisal error:",
                    error
                );


                showError(
                    error
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to reject appraisal."
                );

            }

        };


    // =========================================================
    // CANCEL
    // =========================================================

    const handleDelete =
        async (
            item
        ) => {

            const id =
                item?.id;


            if (!id) {
                return;
            }


            if (
                !window.confirm(
                    "Cancel this appraisal?"
                )
            ) {
                return;
            }


            try {

                await deleteAppraisal(
                    id
                );


                showSuccess(
                    "Appraisal cancelled successfully."
                );


                await loadData();

            } catch (
                error
            ) {

                console.error(
                    "Cancel appraisal error:",
                    error
                );


                showError(
                    error
                        ?.response
                        ?.data
                        ?.message ||
                    "Unable to cancel appraisal."
                );

            }

        };


    // =========================================================
    // COLUMNS
    // =========================================================
    //
    // IMPORTANT:
    // Do NOT create another inline columns array here.
    // Use ./columns.jsx only.
    // =========================================================

    const columns =
        useMemo(
            () => {

                return appraisalColumns({

                    onView:
                        handleView,

                    onEdit:
                        handleEdit,

                    onSubmit:
                        handleSubmit,

                    onApprove:
                        handleApprove,

                    onReject:
                        handleReject,

                    onDelete:
                        handleDelete,

                    currentUser,

                });

            },
            [
                currentUser,
                navigate,
            ]
        );


    // =========================================================
    // MODAL CLOSE
    // =========================================================

    const handleModalClose =
        () => {

            setOpenModal(
                false
            );

            setSelectedItem(
                null
            );

        };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            className="
                mt-5
                space-y-5
            "
        >

            {/* =================================================
                HEADER
            ================================================= */}

            <div
                className="
                    flex
                    flex-col
                    gap-4
                    rounded-2xl
                    bg-white
                    p-5
                    shadow-sm
                    md:flex-row
                    md:items-center
                    md:justify-between
                "
            >

                <div>

                    <h1
                        className="
                            text-2xl
                            font-bold
                            text-navy-700
                        "
                    >
                        Appraisal Management
                    </h1>


                    <p
                        className="
                            mt-1
                            text-sm
                            text-gray-500
                        "
                    >
                        Manage school appraisals,
                        books, competitors,
                        discounts and expected orders.
                    </p>

                </div>


            </div>


            {/* =================================================
                FILTERS
            ================================================= */}

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    rounded-2xl
                    bg-white
                    p-4
                    shadow-sm
                    md:grid-cols-2
                "
            >

                {/* SEARCH */}

                <input
                    type="text"
                    placeholder="
                        Search school,
                        employee,
                        competitor...
                    "
                    value={
                        search
                    }
                    onChange={
                        (e) => {

                            setSearch(
                                e.target.value
                            );

                            setPage(
                                1
                            );

                        }
                    }
                    className="
                        rounded-xl
                        border
                        border-gray-300
                        px-4
                        py-3
                        text-sm
                        outline-none
                        focus:border-brand-500
                        focus:ring-1
                        focus:ring-brand-500
                    "
                />


                {/* STATUS */}

                <select
                    value={
                        status
                    }
                    onChange={
                        (e) => {

                            setStatus(
                                e.target.value
                            );

                            setPage(
                                1
                            );

                        }
                    }
                    className="
                        rounded-xl
                        border
                        border-gray-300
                        bg-white
                        px-4
                        py-3
                        text-sm
                        outline-none
                        focus:border-brand-500
                        focus:ring-1
                        focus:ring-brand-500
                    "
                >

                    <option value="">
                        All Status
                    </option>

                    <option value="draft">
                        Draft
                    </option>

                    <option value="submitted">
                        Submitted
                    </option>

                    <option value="approved">
                        Approved
                    </option>

                    <option value="rejected">
                        Rejected
                    </option>

                    <option value="cancelled">
                        Cancelled
                    </option>

                </select>

            </div>


            {/* =================================================
                TABLE
            ================================================= */}

            <DataTable
                title="Appraisals"
                columns={
                    columns
                }
                data={
                    appraisals
                }
                loading={
                    loading
                }
                onAdd={
                    handleAdd
                }
                addButtonText="Add Appraisal"
            />


            {/* =================================================
                MODAL
            ================================================= */}

            <AppraisalModal
                open={
                    openModal
                }

                item={
                    selectedItem
                }

                onClose={
                    handleModalClose
                }

                reload={
                    loadData
                }
            />

        </div>

    );

}